import {
  Body,
  Brand,
  ContractDetails,
  DeliveryDates,
  Equipment,
  Garage,
  GarageChain,
  Gearbox,
  Generation,
  Key,
  Motorization,
  Network,
  Organization,
  PhoneInput,
  Place,
  PremiumService,
  RentalCar,
  Service,
  Slot,
  User,
} from "@App/types";
import { Action } from "@App/state/actions";
import { ActionType } from "@App/state/action-types";

const getOrganization = (): Organization => {
  return (
    window.organization ?? {
      id: 1,
      uuid: "6992a5a7-219e-43d8-be0f-a00e4eda5be0",
      name: "repareo",
    }
  );
};

export interface OrderReducerState {
  organization: Organization;
  network: Network | null;
  service: Service | null;
  place?: Place | null;
  garage?: Garage | null;
  premiumServices: PremiumService[];
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: PhoneInput | null;
  user: User | null;
  slot: Slot | null;
  brand: Brand | null;
  generation: Generation | null;
  motorization: Motorization | null;
  key: Key | null;
  body: Body | null;
  gearbox: Gearbox | null;
  mileage: number | null;
  initialRegistrationDate: string | null;
  equipments: Equipment[] | null;
  subRepairs: string[] | null;
  // vehicle delivery
  contractDetails: ContractDetails | null;
  deliveryDates: DeliveryDates | null;
  garage_chain: GarageChain[] | null;
  numberPlate: string | null;
  rentalCarSelection: RentalCar | null;
  vin: string | null;
  claimNumber: string | null;
  hsntsn: string | null;
  contractNumber: string | null;
  message: string;
  address: {
    street?: string;
    city?: string;
    postcode?: string;
    addition?: string;
    userConfirmedAddress?: boolean;
  };
  mustUseAuthorizedGarage: boolean;
  reviewOptIn: boolean;
}

const initialState: OrderReducerState = {
  organization: getOrganization(),
  network: null,
  service: null,
  // vehicle
  brand: null,
  generation: null,
  motorization: null,
  key: {
    hsn: "",
    tsn: "",
  },
  body: null,
  gearbox: null,
  mileage: null,
  initialRegistrationDate: null,
  equipments: [],
  // place
  place: null,
  garage: null,
  premiumServices: [],
  firstName: "",
  lastName: "",
  email: "",
  rentalCarSelection: null,
  phone: {
    country: "+49",
    number: null,
  },
  user: null,
  slot: null,
  subRepairs: [],
  // vehicle delivery
  contractDetails: {
    carId: null,
    agreementNumber: null,
    name: null,
    street: null,
    postcode: null,
    city: null,
    phone: null,
  },
  deliveryDates: {
    date1: null,
    date2: null,
    date3: null,
  },
  garage_chain: null,
  numberPlate: "",
  vin: null,
  claimNumber: null,
  hsntsn: null,
  contractNumber: null,
  message: "",
  address: {
    street: "",
    city: "",
    postcode: "",
    addition: "",
    userConfirmedAddress: false,
  },
  mustUseAuthorizedGarage: false,
  reviewOptIn: false,
};

const orderReducer = (
  state: OrderReducerState = initialState,
  action: Action,
): OrderReducerState => {
  switch (action.type) {
    // organization actions
    case ActionType.CHOOSE_ORGANIZATION:
      return {
        ...state,
        organization: action.payload,
      };
    // network actions
    case ActionType.SET_NETWORK:
      return {
        ...state,
        network: action.payload,
      };
    // service actions
    case ActionType.CHOOSE_SERVICE:
      return {
        ...state,
        service: action.payload,
      };
    case ActionType.REMOVE_SERVICE:
      return {
        ...state,
        service: null,
      };
    // brand actions
    case ActionType.CHOOSE_BRAND:
      return {
        ...state,
        brand: action.payload,
      };
    // generation actions
    case ActionType.CHOOSE_GENERATION:
      return {
        ...state,
        generation: action.payload,
      };
    // motorization actions
    case ActionType.CHOOSE_MOTORIZATION:
      return {
        ...state,
        motorization: action.payload,
      };
    case ActionType.SET_KEY:
      return {
        ...state,
        key: action.payload,
      };
    // body actions
    case ActionType.CHOOSE_BODY:
      return {
        ...state,
        body: action.payload,
      };
    // gearbox actions
    case ActionType.CHOOSE_GEARBOX:
      return {
        ...state,
        gearbox: action.payload,
      };
    // mileage actions
    case ActionType.SET_MILEAGE:
      return {
        ...state,
        mileage: action.payload,
      };
    // initial registration date actions
    case ActionType.SET_REGISTRATION_DATE:
      return {
        ...state,
        initialRegistrationDate: action.payload,
      };
    // equipment actions
    case ActionType.ADD_EQUIPMENT:
      return {
        ...state,
        equipments: [...(state.equipments ?? []), action.payload],
      };
    case ActionType.REMOVE_EQUIPMENT:
      return {
        ...state,
        equipments:
          state.equipments?.filter((equipment) => equipment.id !== action.payload.id) ?? [],
      };
    // place actions
    case ActionType.CHOOSE_PLACE:
      return {
        ...state,
        place: action.payload,
      };
    // garage actions
    case ActionType.CHOOSE_GARAGE:
      return {
        ...state,
        garage: action.payload,
      };
    case ActionType.REMOVE_GARAGE:
      return {
        ...state,
        garage: null,
      };
    case ActionType.CHOOSE_SLOT:
      return {
        ...state,
        slot: action.payload,
      };
    case ActionType.ADD_PREMIUM_SERVICE:
      return state.premiumServices.some((p) => p.type === action.payload.type)
        ? { ...state }
        : { ...state, premiumServices: [...state.premiumServices, action.payload] };

    case ActionType.REMOVE_PREMIUM_SERVICE:
      return {
        ...state,
        premiumServices: state.premiumServices.filter(
          (premiumService) => premiumService.type !== action.payload.type,
        ),
      };
    case ActionType.CLEAR_PREMIUM_SERVICES:
      return {
        ...state,
        premiumServices: action.payload,
      };
    case ActionType.SET_FIRST_NAME:
      return {
        ...state,
        firstName: action.payload,
      };
    case ActionType.SET_LAST_NAME:
      return {
        ...state,
        lastName: action.payload,
      };
    case ActionType.SET_EMAIL:
      return {
        ...state,
        email: action.payload,
      };
    case ActionType.SET_PHONE:
      return {
        ...state,
        phone: action.payload,
      };
    case ActionType.SET_NUMBERPLATE:
      return {
        ...state,
        numberPlate: action.payload,
      };
    case ActionType.SET_CLAIM_NUMBER:
      return {
        ...state,
        claimNumber: action.payload,
      };
    case ActionType.SET_CONTRACT_NUMBER:
      return {
        ...state,
        contractNumber: action.payload,
      };
    // subrepair actions
    case ActionType.ADD_SUB_REPAIR:
      return {
        ...state,
        subRepairs: [...(state.subRepairs ?? []), action.payload],
      };
    case ActionType.REMOVE_SUB_REPAIR:
      return {
        ...state,
        subRepairs: state.subRepairs?.filter((subRepair) => subRepair !== action.payload) ?? [],
      };
    // vehicle delivery actions
    case ActionType.SET_CONTRACT_DETAILS:
      return {
        ...state,
        contractDetails: action.payload,
      };
    case ActionType.UPDATE_CONTRACT_DETAILS:
      return {
        ...state,
        contractDetails: action.payload,
      };
    case ActionType.SET_DELIVERY_DATES:
      return {
        ...state,
        deliveryDates: action.payload,
      };
    case ActionType.SET_GARAGE_CHAINS:
      return {
        ...state,
        garage_chain: action.payload,
      };
    case ActionType.SET_RENTAL_CAR_SELECTION:
      return {
        ...state,
        rentalCarSelection: action.payload,
      };
    case ActionType.SET_VIN:
      return {
        ...state,
        vin: action.payload,
      };
    case ActionType.SET_HSNTSN:
      return {
        ...state,
        hsntsn: action.payload,
      };
    case ActionType.SET_MESSAGE:
      return {
        ...state,
        message: action.payload,
      };
    case ActionType.SET_STREET:
      return {
        ...state,
        address: {
          ...state.address,
          street: action.payload,
        },
      };
    case ActionType.SET_CITY:
      return {
        ...state,
        address: {
          ...state.address,
          city: action.payload,
        },
      };
    case ActionType.SET_POSTCODE:
      return {
        ...state,
        address: {
          ...state.address,
          postcode: action.payload,
        },
      };
    case ActionType.SET_ADDITION:
      return {
        ...state,
        address: {
          ...state.address,
          addition: action.payload,
        },
      };
    case ActionType.SET_USER_CONFIRMED_ADDRESS:
      return {
        ...state,
        address: {
          ...state.address,
          userConfirmedAddress: action.payload,
        },
      };
    case ActionType.SET_MUST_USE_AUTHORIZED_GARAGE:
      return {
        ...state,
        mustUseAuthorizedGarage: action.payload,
      };
    case ActionType.SET_REVIEW_OPT_IN:
      return {
        ...state,
        reviewOptIn: action.payload,
      };
  }
  return state;
};

export default orderReducer;
